import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RenameInput from "./RenameInput";
import "../../assets/styles/colors.css";

const controlsStyle = `
  .react-flow__minimap {
    background: white !important;
  }
  .react-flow__minimap-canvas {
    background: white !important;
  }
  .dark .react-flow__minimap {
    background: rgb(15 23 42) !important;
  }
  .dark .react-flow__minimap-canvas {
    background: rgb(15 23 42) !important;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = controlsStyle;
  if (!document.head.querySelector("style[data-topology-controls]")) {
    styleSheet.setAttribute("data-topology-controls", "true");
    document.head.appendChild(styleSheet);
  }
}

// View modes cycle: icon -> compact -> full -> icon ...
const VIEW_MODES = ["icon", "compact", "full"];
const VIEW_MODE_LABELS = {
  icon: "Showing icons only — click for main fields",
  compact: "Showing main fields — click for all fields",
  full: "Showing all fields — click for icons only",
};

// Sidebar icon button
function SideBtn({ onClick, title, active, children, ...rest }) {
  return (
    <button
      onClick={onClick}
      {...rest}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200
        ${
          active
            ? "bg-(--color-primary)/20 text-(--color-primary)"
            : "text-(--color-primary) hover:bg-(--color-primary)/15 active:bg-(--color-primary)/25"
        }`}
    >
      {children}
    </button>
  );
}

function TopologyCanvas({
  nodes,
  edges,
  nodeTypes,
  reactFlowWrapper,
  reactFlowInstance,
  editingNodeId,
  editingLabel,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onDrop,
  onDragOver,
  onNodeContextMenu,
  onNodeDoubleClick,
  onEdgeClick,
  onLabelChange,
  onSaveRename,
  onCancelRename,
  onAutoLayout,
  hideUI = false,
  loading = false,
}) {
  const [isDarkMode, setIsDarkMode] = useState(
    () =>
      typeof window !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDarkMode(el.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  const [showMiniMap, setShowMiniMap] = useState(true);
  // Three-state view: "icon" (icons only), "compact" (main fields), "full" (all fields)
  const [viewMode, setViewMode] = useState("compact");
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMatchIdx, setSelectedMatchIdx] = useState(0);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const hoverCloseTimeoutRef = useRef(null);
  const flowRef = useRef(null);

  const cycleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const idx = VIEW_MODES.indexOf(prev);
      return VIEW_MODES[(idx + 1) % VIEW_MODES.length];
    });
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
    setSelectedMatchIdx(0);
  }, []);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  const handleSearchMouseEnter = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
    if (!searchOpen) openSearch();
  }, [searchOpen, openSearch]);

  const handleSearchMouseLeave = useCallback(() => {
    hoverCloseTimeoutRef.current = setTimeout(() => {
      if (!searchQuery.trim()) setSearchOpen(false);
    }, 150);
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current)
        clearTimeout(hoverCloseTimeoutRef.current);
    };
  }, []);

  const searchMatches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return nodes.filter((n) => {
      const d = n.data || {};
      return (
        (d.label && d.label.toLowerCase().includes(q)) ||
        (d.hostname && d.hostname.toLowerCase().includes(q)) ||
        (d.ip_address && d.ip_address.toLowerCase().includes(q)) ||
        (d.nodeType && d.nodeType.toLowerCase().includes(q))
      );
    });
  }, [nodes, searchQuery]);

  const zoomToNode = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node || !reactFlowInstance?.current) return;
      const x = node.position.x + (node.measured?.width ?? 150) / 2;
      const y = node.position.y + (node.measured?.height ?? 50) / 2;
      reactFlowInstance.current.setCenter(x, y, { zoom: 1.5, duration: 400 });
    },
    [nodes, reactFlowInstance],
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        closeSearch();
        return;
      }
      if (!searchMatches.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMatchIdx((prev) => (prev + 1) % searchMatches.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMatchIdx(
          (prev) => (prev - 1 + searchMatches.length) % searchMatches.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = searchMatches[selectedMatchIdx];
        if (target) {
          zoomToNode(target.id);
          closeSearch();
        }
      }
    },
    [searchMatches, selectedMatchIdx, zoomToNode, closeSearch],
  );

  useEffect(() => {
    setSelectedMatchIdx(0);
  }, [searchMatches.length]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        openSearch();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [openSearch]);

  useEffect(() => {
    if (!isExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);

  // Zoom helpers via reactFlowInstance ref
  const handleZoomIn = useCallback(() => {
    reactFlowInstance?.current?.zoomIn({ duration: 200 });
  }, [reactFlowInstance]);

  const handleZoomOut = useCallback(() => {
    reactFlowInstance?.current?.zoomOut({ duration: 200 });
  }, [reactFlowInstance]);

  const handleFitView = useCallback(() => {
    reactFlowInstance?.current?.fitView({ duration: 300, padding: 0.1 });
  }, [reactFlowInstance]);

  const patchedNodeTypes = useMemo(() => {
    const patched = {};
    Object.entries(nodeTypes).forEach(([type, Comp]) => {
      patched[type] = (props) => <Comp {...props} viewMode={viewMode} />;
    });
    return patched;
  }, [nodeTypes, viewMode]);

  const canvasContent = (
    <div
      className={`bg-(--color-card) h-full flex flex-col relative overflow-hidden rounded-xl p-3 sm:p-7 border border-(--color-border) shadow-[0_0_20px_var(--color-shadow-primary)] ${
        hideUI ? "hide-react-flow-attribution" : ""
      } w-full min-w-0`}
      style={{ borderRadius: "1rem", minHeight: isExpanded ? "100%" : 200 }}
      ref={reactFlowWrapper}
      data-tour="topology-canvas"
    >
      {/* ── LEFT SIDEBAR: filter + divider + zoom controls ── */}
      {!hideUI && (
        <div
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-1.5
          bg-(--color-bg-card) border border-(--color-primary)/25 rounded-md px-1 py-2"
          data-tour="canvas-controls-zoom"
        >
          {/* View mode cycle toggle: icon -> compact -> full */}
          <SideBtn
            data-tour="canvas-filter-toggle"
            onClick={cycleViewMode}
            title={VIEW_MODE_LABELS[viewMode]}
            active={viewMode !== "icon"}
          >
            {viewMode === "icon" ? (
              // icon-only state: show a simple "box/device" glyph
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="12" rx="2" />
                <line x1="8" y1="20" x2="16" y2="20" />
                <line x1="12" y1="16" x2="12" y2="20" />
              </svg>
            ) : viewMode === "compact" ? (
              // compact state: list with a few lines (main fields)
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="14" y2="12" />
              </svg>
            ) : (
              // full state: full list (all fields)
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="10" x2="20" y2="10" />
                <line x1="4" y1="14" x2="20" y2="14" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            )}
          </SideBtn>

          {/* Divider */}
          <div className="w-5 h-px bg-(--color-primary)/20 my-0.5" />

          {/* Zoom In */}
          <SideBtn onClick={handleZoomIn} title="Zoom in">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </SideBtn>

          {/* Zoom Out */}
          <SideBtn onClick={handleZoomOut} title="Zoom out">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </SideBtn>

          {/* Fit View */}
          <SideBtn onClick={handleFitView} title="Fit to view">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 7V5a2 2 0 0 1 2-2h2" />
              <path d="M17 3h2a2 2 0 0 1 2 2v2" />
              <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
              <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            </svg>
          </SideBtn>

          {/* Auto Layout */}
          {onAutoLayout && (
            <SideBtn onClick={onAutoLayout} title="Auto layout">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="4" rx="1" />
                <rect x="14" y="3" width="7" height="4" rx="1" />
                <rect x="8" y="17" width="8" height="4" rx="1" />
                <line x1="6.5" y1="7" x2="6.5" y2="12" />
                <line x1="17.5" y1="7" x2="17.5" y2="12" />
                <line x1="6.5" y1="12" x2="17.5" y2="12" />
                <line x1="12" y1="12" x2="12" y2="17" />
              </svg>
            </SideBtn>
          )}

          {/* Divider */}
          <div className="w-5 h-px bg-(--color-primary)/20 my-0.5" />

          {/* Expand / Collapse */}
          <SideBtn
            onClick={handleToggleExpand}
            data-tour="canvas-expand"
            title={isExpanded ? "Collapse view" : "Expand view"}
            active={isExpanded}
          >
            {isExpanded ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 3 9 9 3 9" />
                <polyline points="15 3 15 9 21 9" />
                <polyline points="9 21 9 15 3 15" />
                <polyline points="15 21 15 15 21 15" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </SideBtn>
        </div>
      )}

      {/* ── TOP-RIGHT: search + expand (unchanged) ── */}
      {!hideUI && (
        <div className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 flex items-center">
          {/* Node search */}
          <div
            ref={searchContainerRef}
            data-tour="canvas-search"
            className="relative"
            onMouseEnter={handleSearchMouseEnter}
            onMouseLeave={handleSearchMouseLeave}
          >
            <div
              role="button"
              tabIndex={searchOpen ? -1 : 0}
              aria-label={searchOpen ? undefined : "Search nodes"}
              aria-expanded={searchOpen}
              className={`relative flex items-center h-9 border rounded-md overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                searchOpen
                  ? "w-65 bg-(--color-bg-card) border-(--color-primary)/40 pl-2.5 pr-1"
                  : "w-9 border-transparent hover:bg-(--color-primary)/15 active:bg-(--color-primary)/25 cursor-pointer"
              }`}
              onClick={() => {
                if (!searchOpen) openSearch();
              }}
              onKeyDown={(e) => {
                if (!searchOpen && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  openSearch();
                }
              }}
              title={!searchOpen ? "Search nodes (Ctrl+F)" : undefined}
            >
              <span
                className={`flex items-center justify-center transition-all duration-300 ${searchOpen ? "relative w-4.5 h-4.5" : "absolute inset-0 pointer-events-none"}`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`shrink-0 transition-colors duration-300 ${searchOpen ? "text-(--color-text-muted)" : "text-(--color-primary)"}`}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search nodes..."
                tabIndex={searchOpen ? 0 : -1}
                aria-hidden={!searchOpen}
                className={`bg-transparent border-none outline-none text-sm text-(--color-text-white) placeholder-(--color-text-muted) min-w-0 transition-[opacity,margin,flex] duration-300 ${
                  searchOpen
                    ? "opacity-100 ml-2 flex-1"
                    : "opacity-0 ml-0 flex-none w-0 pointer-events-none"
                }`}
              />
              {searchOpen && searchQuery && (
                <span className="shrink-0 text-[11px] font-medium text-(--color-text-muted) whitespace-nowrap mr-2">
                  {searchMatches.length
                    ? `${selectedMatchIdx + 1}/${searchMatches.length}`
                    : "0"}
                </span>
              )}
              <button
                type="button"
                tabIndex={searchOpen ? 0 : -1}
                aria-label="Close search"
                className={`shrink-0 w-7 h-7 flex items-center justify-center rounded text-(--color-text-muted) hover:text-(--color-primary) hover:bg-(--color-primary)/10 transition-all duration-200 ${
                  searchOpen
                    ? "opacity-100 delay-150"
                    : "opacity-0 pointer-events-none w-0"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  closeSearch();
                }}
                title="Close search"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {searchOpen && searchQuery.trim() && searchMatches.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1.5 bg-(--color-bg-card) border border-(--color-border) rounded-md shadow-lg max-h-55 overflow-y-auto animate-[fadeIn_0.18s_ease-out]">
                {searchMatches.map((node, idx) => (
                  <button
                    key={node.id}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${
                      idx === selectedMatchIdx
                        ? "bg-(--color-primary)/15 text-(--color-primary)"
                        : "text-(--color-text-white) hover:bg-(--color-primary)/5"
                    }`}
                    onClick={() => {
                      setSelectedMatchIdx(idx);
                      zoomToNode(node.id);
                      closeSearch();
                    }}
                  >
                    <span className="font-medium truncate">
                      {node.data?.label || node.id}
                    </span>
                    {node.data?.ip_address && (
                      <span className="text-xs font-mono text-(--color-text-muted) shrink-0">
                        {node.data.ip_address}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {searchOpen && searchQuery.trim() && searchMatches.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1.5 bg-(--color-bg-card) border border-(--color-border) rounded-md shadow-lg px-3 py-2 text-sm text-(--color-text-muted) animate-[fadeIn_0.18s_ease-out]">
                No nodes found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && !hideUI && (
        <div
          className={`absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl backdrop-blur-xs ${isDarkMode ? "bg-black" : "bg-white"}`}
        >
          <div className="relative">
            <div className="w-16 h-16 border-4 border-(--color-primary)/20 border-t-(--color-primary) rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 bg-(--color-primary) rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-4 text-(--color-primary) text-sm animate-pulse">
            Loading topology...
          </p>
        </div>
      )}

      <div className="w-full flex-1 min-h-75" style={{ minWidth: 0 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={patchedNodeTypes}
          defaultEdgeOptions={{
            style: {
              stroke: isDarkMode ? "var(--color-primary)" : "#16a34a",
              strokeWidth: 2,
            },
          }}
          minZoom={0.1}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeContextMenu={onNodeContextMenu}
          onNodeDoubleClick={onNodeDoubleClick}
          onEdgeClick={onEdgeClick}
          onInit={(instance) => (reactFlowInstance.current = instance)}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          {!hideUI && (
            <Background gap={16} size={1} color="var(--color-primary-50)" />
          )}
          {!hideUI && showMiniMap && (
            <MiniMap
              data-tour="canvas-minimap"
              pannable
              zoomable
              style={{
                width: 120,
                height: 80,
                backgroundColor: isDarkMode
                  ? "rgb(15 23 42 / 0.95)"
                  : "rgb(255 255 255 / 0.95)",
                border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                borderRadius: "8px",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgb(0 0 0 / 0.3)"
                  : "0 1px 3px rgb(0 0 0 / 0.1)",
              }}
              nodeColor={() => "var(--color-primary)"}
              maskColor={
                isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.1)"
              }
            />
          )}

          {/* MiniMap Toggle — bottom-right */}
          {!hideUI && (
            <button
              onClick={() => setShowMiniMap(!showMiniMap)}
              className="absolute bottom-3 right-3 z-10 p-1.5 bg-(--color-bg-card) border border-(--color-primary)/30 rounded text-(--color-primary) hover:bg-(--color-primary)/10 hover:border-(--color-primary)/50 transition-all"
              title={showMiniMap ? "Hide MiniMap" : "Show MiniMap"}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <rect
                  x="12"
                  y="12"
                  width="7"
                  height="7"
                  rx="1"
                  opacity={showMiniMap ? 1 : 0.3}
                />
              </svg>
            </button>
          )}

          {!hideUI && (
            <RenameInput
              editingNodeId={editingNodeId}
              editingLabel={editingLabel}
              nodes={nodes}
              reactFlowInstance={reactFlowInstance.current}
              reactFlowWrapper={reactFlowWrapper.current}
              onLabelChange={onLabelChange}
              onSave={onSaveRename}
              onCancel={onCancelRename}
            />
          )}
        </ReactFlow>
      </div>
    </div>
  );

  if (isExpanded) {
    return (
      <>
        <div style={{ minHeight: 200 }} />
        {createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsExpanded(false)}
            />
            <div className="relative w-[85vw] h-[90vh] rounded-2xl overflow-hidden shadow-2xl">
              {canvasContent}
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return canvasContent;
}

export default TopologyCanvas;